package ai.jessie.web.settings

import ai.jessie.shared.settings.OrganisationSettingsResponse
import ai.jessie.shared.settings.VatRateResponse
import ai.jessie.web.theme.BadgeTone
import ai.jessie.web.theme.CalendarScrollStyle
import ai.jessie.web.theme.CardHeadStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.HeadTableRowVariant
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.TableNumStyle
import ai.jessie.web.theme.TableRowKind
import ai.jessie.web.theme.TableRowStyle
import ai.jessie.web.theme.TableStyle
import ai.jessie.web.theme.VatTableRowStyle
import ai.jessie.web.theme.VatTableStyle
import ai.jessie.web.theme.badge
import ai.jessie.web.theme.card
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyleVariant
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private const val VAT_TITLE = "MWST-Versionen"

// Read-only versions as on `23-EinstellungenMwst.png`: the version valid on the organisation's
// current date carries the "Aktuell" badge. Only returned versions are listed.
@Composable
internal fun vatRateTable(current: OrganisationSettingsResponse) {
    if (current.vatRates.isEmpty()) {
        card(VAT_TITLE) { P(attrs = HintStyle.toAttrs()) { Text("Keine MWST-Versionen.") } }
        return
    }
    Div(attrs = CardStyle.toAttrs()) {
        Div(attrs = CardHeadStyle.toAttrs()) { H2 { Text(VAT_TITLE) } }
        Div(attrs = CalendarScrollStyle.toAttrs()) {
            Div(
                attrs =
                    TableStyle.toModifier().then(VatTableStyle.toModifier()).toAttrs {
                        attr("role", "table")
                        attr("aria-label", VAT_TITLE)
                    }
            ) {
                vatRow(HeadTableRowVariant) {
                    listOf("Name", "Satz", "Gültig ab", "Gültig bis", "").forEach {
                        Span(attrs = { attr("role", "columnheader") }) { Text(it) }
                    }
                }
                current.vatRates.forEach { vatEntryRow(it, current.currentDate) }
            }
        }
    }
}

@Composable
private fun vatEntryRow(rate: VatRateResponse, currentDate: String?) {
    vatRow(null) {
        Span(attrs = { attr("role", "cell") }) { Text(rate.name) }
        Span(attrs = TableNumStyle.toAttrs { attr("role", "cell") }) { Text("${rate.rate} %") }
        Span(attrs = TableNumStyle.toAttrs { attr("role", "cell") }) { Text(rate.effectiveFrom) }
        Span(attrs = TableNumStyle.toAttrs { attr("role", "cell") }) {
            Text(rate.effectiveTo ?: "offen")
        }
        Span(attrs = { attr("role", "cell") }) {
            if (rate.isCurrentOn(currentDate)) badge("Aktuell", BadgeTone.PRIMARY)
        }
    }
}

// ISO dates compare lexically; an open end is valid until further notice.
private fun VatRateResponse.isCurrentOn(date: String?): Boolean =
    date != null && effectiveFrom <= date && (effectiveTo == null || effectiveTo!! >= date)

@Composable
private fun vatRow(variant: CssStyleVariant<TableRowKind>?, cells: @Composable () -> Unit) {
    Div(
        attrs =
            TableRowStyle.toModifier(variant).then(VatTableRowStyle.toModifier()).toAttrs {
                attr("role", "row")
            }
    ) {
        cells()
    }
}
